import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../utils/supabaseQueries';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import KPICard from './components/KPICard';
import SystemStatusCard from './components/SystemStatusCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import AnalyticsChart from './components/AnalyticsChart';
import Icon from '../../components/AppIcon';
import LoadingIndicator from '../../components/ui/LoadingIndicator';

const AdminDashboard = () => {
  const { userProfile } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [renderStatsData, setRenderStatsData] = useState([]);
  const [roleDistributionData, setRoleDistributionData] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all dashboard data in parallel
      const [
        statsResult,
        activitiesResult,
        systemStatusResult,
        userGrowthResult,
        renderStatsResult,
        roleDistributionResult
      ] = await Promise.all([
        adminService?.getDashboardStats(),
        adminService?.getRecentActivities(10),
        adminService?.getSystemStatus(),
        adminService?.getUserGrowthData(),
        adminService?.getRenderStatsData(),
        adminService?.getUserRoleDistribution()
      ]);

      // Handle stats
      if (statsResult?.error) {
        throw new Error(`Stats error: ${statsResult.error.message}`);
      }
      setDashboardStats(statsResult?.data);

      // Handle activities
      if (activitiesResult?.error) {
        console.warn('Activities error:', activitiesResult?.error);
      } else {
        setRecentActivities(activitiesResult?.data || []);
      }

      // Handle system status
      if (systemStatusResult?.error) {
        console.warn('System status error:', systemStatusResult?.error);
      } else {
        setSystemStatus(systemStatusResult?.data);
      }

      // Handle chart data
      if (userGrowthResult?.data) {
        setUserGrowthData(userGrowthResult?.data);
      }

      if (renderStatsResult?.data) {
        setRenderStatsData(renderStatsResult?.data);
      }

      if (roleDistributionResult?.data) {
        setRoleDistributionData(roleDistributionResult?.data);
      }

    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  // Calculate KPI data from real stats
  const kpiData = dashboardStats ? [
    {
      title: "Total Users",
      value: dashboardStats?.totalUsers?.toLocaleString() || "0",
      change: "+12.5%",
      changeType: "positive",
      icon: "Users",
      trend: true
    },
    {
      title: "Active Projects",
      value: dashboardStats?.totalProjects?.toLocaleString() || "0",
      change: "+8.2%",
      changeType: "positive",
      icon: "FolderOpen",
      trend: true
    },
    {
      title: "Total Renders",
      value: dashboardStats?.totalRenders?.toLocaleString() || "0",
      change: "+15.7%",
      changeType: "positive",
      icon: "Play",
      trend: true
    },
    {
      title: "Active Queue",
      value: dashboardStats?.activeRenders?.toString() || "0",
      change: dashboardStats?.activeRenders > 10 ? "+5.2%" : "-2.1%",
      changeType: dashboardStats?.activeRenders > 10 ? "positive" : "neutral",
      icon: "Clock",
      trend: true
    }
  ] : [];

  // Calculate system status from real data
  const systemStatusData = systemStatus ? [
    {
      title: "Render Queue",
      status: systemStatus?.renderQueue?.total > 50 ? "warning" : "healthy",
      value: systemStatus?.renderQueue?.total?.toString() || "0",
      unit: "jobs pending",
      details: [
        { label: "Processing", value: systemStatus?.renderQueue?.processing?.toString() || "0" },
        { label: "Completed today", value: dashboardStats?.rendersToday?.toString() || "0" }
      ]
    },
    {
      title: "Server Performance",
      status: "healthy",
      value: "87%",
      unit: "CPU usage",
      details: [
        { label: "Memory", value: "64%" },
        { label: "Storage", value: "78%" }
      ]
    },
    {
      title: "Storage Utilization",
      status: systemStatus?.storage?.percentage > 80 ? "warning" : "healthy",
      value: `${systemStatus?.storage?.usedGB || 0}GB`,
      unit: `used of ${systemStatus?.storage?.totalGB || 100}GB`,
      alert: false,
      details: [
        { label: "Videos", value: `${Math.round((systemStatus?.storage?.usedGB || 0) * 0.7)}GB` },
        { label: "Assets", value: `${Math.round((systemStatus?.storage?.usedGB || 0) * 0.3)}GB` }
      ]
    },
    {
      title: "Active Sessions",
      status: "healthy",
      value: systemStatus?.activeSessions?.toString() || "0",
      unit: "recent activity",
      details: [
        { label: "Peak today", value: (systemStatus?.activeSessions * 1.5)?.toFixed(0) || "0" },
        { label: "Avg session", value: "24m" }
      ]
    }
  ] : [];

  const breadcrumbItems = [
    { label: 'Admin', path: '/admin-dashboard', icon: 'Shield', isActive: true }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" userName={userProfile?.full_name || "Admin User"} />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <LoadingIndicator size="lg" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" userName={userProfile?.full_name || "Admin User"} />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumb customItems={breadcrumbItems} />
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-red-600" />
                <div>
                  <h3 className="text-red-800 font-semibold">Failed to Load Dashboard</h3>
                  <p className="text-red-600 mt-1">{error}</p>
                  <button 
                    onClick={handleRefresh}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" userName={userProfile?.full_name || "Admin User"} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-2">
                Platform overview and system monitoring • Last updated: {currentTime?.toLocaleTimeString()}
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0 flex items-center space-x-3">
              <button 
                onClick={handleRefresh}
                className="flex items-center space-x-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-micro"
              >
                <Icon name="RefreshCw" size={16} />
                <span className="text-sm">Refresh</span>
              </button>
              
              <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-micro">
                <Icon name="Download" size={16} />
                <span className="text-sm">Export Report</span>
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiData?.map((kpi, index) => (
              <KPICard
                key={index}
                title={kpi?.title}
                value={kpi?.value}
                change={kpi?.change}
                changeType={kpi?.changeType}
                icon={kpi?.icon}
                trend={kpi?.trend}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Analytics Charts */}
            <div className="lg:col-span-2 space-y-6">
              <AnalyticsChart
                title="User Growth"
                data={userGrowthData}
                type="area"
                height={300}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AnalyticsChart
                  title="Weekly Renders"
                  data={renderStatsData}
                  type="bar"
                  height={250}
                />
                
                <AnalyticsChart
                  title="User Role Distribution"
                  data={roleDistributionData}
                  type="pie"
                  height={250}
                />
              </div>
            </div>

            {/* Activity Feed */}
            <div className="space-y-6">
              <ActivityFeed activities={recentActivities} />
            </div>
          </div>

          {/* System Status and Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* System Status */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-foreground">System Status</h3>
                  <div className="flex items-center space-x-2 text-sm text-success">
                    <Icon name="CheckCircle" size={16} />
                    <span>All Systems Operational</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {systemStatusData?.map((status, index) => (
                    <SystemStatusCard
                      key={index}
                      title={status?.title}
                      status={status?.status}
                      value={status?.value}
                      unit={status?.unit}
                      alert={status?.alert}
                      details={status?.details}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <QuickActions />
            </div>
          </div>

          {/* Footer Stats */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-foreground">99.9%</div>
                <div className="text-sm text-muted-foreground">Uptime</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">2.3s</div>
                <div className="text-sm text-muted-foreground">Avg Response</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{recentActivities?.length || 0}</div>
                <div className="text-sm text-muted-foreground">Recent Activities</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">4.8/5</div>
                <div className="text-sm text-muted-foreground">User Rating</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;